'use client';

import { usePathname, useRouter } from 'next/navigation';
import React, { type PropsWithChildren, useMemo } from 'react';
import { isExternalURL } from '@/helpers/common';
import { groupMemberDocumentsRequested } from '@/helpers/user';
import { useGetCommonStore } from '@/store';
import type { MenuClientHideConditions, ServerMenu } from '../helpers';

type MenuLinkClientWrapperProps = {
  id: ServerMenu;
  url: string;
  className?: string;
  hideConditions?: MenuClientHideConditions[];
  hideConditionResults: Record<string, boolean>;
};

export const MenuLinkClientWrapper = React.forwardRef<
  HTMLLIElement,
  PropsWithChildren<MenuLinkClientWrapperProps>
>(
  (
    { id, url, children, className, hideConditions, hideConditionResults },
    ref
  ) => {
    const { push } = useRouter();
    const pathname = usePathname();

    const useCommonStore = useGetCommonStore();
    const user = useCommonStore.use.user();

    const hasDocumentsRequested = useMemo(
      () => groupMemberDocumentsRequested(user),
      [user]
    );

    const checkHideConditions = () =>
      hideConditions?.some((condition) => hideConditionResults[condition]);
    const isHidden = checkHideConditions();

    const isActive =
      url === pathname || (url === '/' && pathname.slice(0, 5) === '/game');

    const handleClickMenuItem = (event: React.MouseEvent) => {
      event.preventDefault();
      isExternalURL(url) ? window.location.replace(url) : push(url);
    };

    if (isHidden) return null;

    return (
      <li
        className={[className, isActive && 'active'].filter(Boolean).join(' ')}
        onClick={handleClickMenuItem}
        ref={ref}
      >
        {children}
        {id === 'profile' && hasDocumentsRequested && (
          <span className="notification" />
        )}
      </li>
    );
  }
);
